import { isPhoneNumberComplete } from '@/lib/phoneNumber';
import type { PhoneAuthStep } from '@/types/phone-auth-flow';
import type { RequestCodeMethod } from '@/types/dto/phone-auth.dto';
import { Phone } from 'lucide-react';
import { useMaskInput } from 'use-mask-input';

interface PhoneAuthStepPanelProps {
  authStep: PhoneAuthStep;
  isRequestingCode: boolean;
  isValidatingCode: boolean;
  nameDescription?: string;
  nameTitle?: string;
  onAuthStepChange: (step: PhoneAuthStep) => void;
  onPhoneNumberChange: (value: string) => void;
  onRequestCode: (method: RequestCodeMethod) => void;
  onUserNameChange: (value: string) => void;
  onValidateCode: () => void;
  onVerificationCodeChange: (value: string) => void;
  phoneNumber: string;
  phoneRequestMethod: RequestCodeMethod | null;
  userName: string;
  verificationCode: string;
}

export function PhoneAuthStepPanel({
  authStep,
  isRequestingCode,
  isValidatingCode,
  nameDescription = 'Seu nome para continuar',
  nameTitle = 'Como podemos te chamar?',
  onAuthStepChange,
  onPhoneNumberChange,
  onRequestCode,
  onUserNameChange,
  onValidateCode,
  onVerificationCodeChange,
  phoneNumber,
  phoneRequestMethod,
  userName,
  verificationCode,
}: PhoneAuthStepPanelProps) {
  const ref = useMaskInput({ mask: ['(99) 9999-9999', '(99) 99999-9999'] });
  const phoneNumberReady = isPhoneNumberComplete(phoneNumber);
  const normalizedCode = verificationCode.replace(/\D/g, '');

  return (
    <div className="animate-fade-in-up space-y-5">
      {authStep === 'phone' && (
        <div className="space-y-4">
          <div className="mb-6 text-center">
            <div className="bg-primary/10 mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl">
              <Phone className="text-primary h-6 w-6" />
            </div>
            <h2 className="text-foreground text-xl font-bold">Qual seu telefone?</h2>
            <p className="text-muted-foreground mt-1 text-sm">Vamos enviar um código de verificação</p>
          </div>
          <input
            ref={ref}
            type="tel"
            aria-label="Telefone"
            placeholder="(11) 99999-0000"
            value={phoneNumber}
            onChange={(event) => onPhoneNumberChange(event.target.value)}
            className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-14 w-full rounded-xl border-none px-4 text-center text-lg font-medium outline-none focus:ring-2"
          />
          <button
            type="button"
            onClick={() => onRequestCode('whatsapp')}
            disabled={!phoneNumberReady || isRequestingCode}
            className="flex h-14 w-full items-center justify-center gap-2.5 rounded-xl bg-[hsl(142,70%,41%)] text-base font-bold text-[hsl(0,0%,100%)] transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            {isRequestingCode && phoneRequestMethod === 'whatsapp' ? 'Enviando...' : 'Enviar pelo WhatsApp'}
          </button>
          <button
            type="button"
            onClick={() => onRequestCode('sms')}
            disabled={!phoneNumberReady || isRequestingCode}
            className="border-border bg-muted text-muted-foreground hover:bg-accent/10 hover:text-foreground flex h-12 w-full items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-all disabled:opacity-40"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {isRequestingCode && phoneRequestMethod === 'sms' ? 'Enviando...' : 'Enviar por SMS'}
          </button>
        </div>
      )}

      {authStep === 'code' && (
        <div className="space-y-4">
          <div className="mb-6 text-center">
            <h2 className="text-foreground text-xl font-bold">Digite o código</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {phoneRequestMethod === 'sms' ? 'Enviamos por SMS para' : 'Enviamos para'} {phoneNumber}
            </p>
          </div>
          <input
            type="text"
            aria-label="Código de verificação"
            placeholder="000000"
            maxLength={6}
            value={verificationCode}
            onChange={(event) => onVerificationCodeChange(event.target.value.replace(/\D/g, ''))}
            className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-14 w-full rounded-xl border-none px-3 text-center text-xl font-bold tracking-[0.35em] outline-none placeholder:tracking-[0.35em] focus:ring-2 sm:px-4 sm:text-2xl sm:tracking-[0.5em] sm:placeholder:tracking-[0.5em]"
          />
          <button
            type="button"
            onClick={onValidateCode}
            disabled={normalizedCode.length < 6 || isValidatingCode}
            className="bg-primary text-primary-foreground h-14 w-full rounded-xl text-base font-bold transition-all hover:opacity-90 disabled:opacity-40"
          >
            {isValidatingCode ? 'Validando...' : 'Verificar'}
          </button>
          <button
            type="button"
            onClick={() => onAuthStepChange('phone')}
            disabled={isValidatingCode}
            className="text-muted-foreground hover:text-foreground w-full text-sm transition-colors"
          >
            Trocar número
          </button>
        </div>
      )}

      {authStep === 'name' && (
        <div className="space-y-4">
          <div className="mb-6 text-center">
            <h2 className="text-foreground text-xl font-bold">{nameTitle}</h2>
            <p className="text-muted-foreground mt-1 text-sm">{nameDescription}</p>
          </div>
          <input
            type="text"
            aria-label="Nome"
            placeholder="Seu nome"
            value={userName}
            onChange={(event) => onUserNameChange(event.target.value)}
            className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-14 w-full rounded-xl border-none px-4 text-center text-lg font-medium outline-none focus:ring-2"
          />
        </div>
      )}
    </div>
  );
}
